// Shared helpers for the Daily Work Report (DWR) screens.
import { useEffect, useState } from "react";
import api from "@/lib/api";

export const STATUS_LABEL = {
  pending: "Pending", in_progress: "In Progress", completed: "Completed", delayed: "Delayed",
  waiting_customer: "Waiting for Customer", waiting_materials: "Waiting for Materials", cancelled: "Cancelled",
};
export const TASK_STATUSES = Object.keys(STATUS_LABEL);

// Same colour language as the rest of KARIS: green done, amber waiting, red problem.
export const STATUS_TONE = {
  completed: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  in_progress: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30",
  pending: "bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30",
  waiting_customer: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
  waiting_materials: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
  delayed: "bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/30",
  cancelled: "bg-muted text-muted-foreground border-border",
};

export const REPORT_STATUS = {
  draft: { label: "Draft", tone: "bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30" },
  submitted: { label: "Waiting for review", tone: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30" },
  approved: { label: "Approved", tone: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30" },
  returned: { label: "Returned for correction", tone: "bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/30" },
  missing: { label: "Not submitted", tone: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30" },
};

export const PRIORITY_LABEL = { low: "Low", medium: "Medium", high: "High", critical: "Critical" };
export const CATEGORY_LABEL = {
  installation: "Installation", maintenance: "Maintenance", customer_visit: "Customer visit",
  support: "Support / troubleshooting", office: "Office / admin", other: "Other",
};
export const DELAY_LABEL = {
  customer: "Customer-related", staff: "KaribuNet staff", supplier: "Supplier", material_unavailable: "Material unavailable",
  transport: "Transport issue", network: "Internet / network issue", weather_site: "Weather or site condition",
  approval_pending: "Approval pending", other: "Other",
};

export const todayISO = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

export const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return isNaN(d) ? iso : d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
};

export const duration = (start, end) => {
  if (!start || !end) return "";
  const [h1, m1] = start.split(":").map(Number), [h2, m2] = end.split(":").map(Number);
  const mins = h2 * 60 + m2 - (h1 * 60 + m1);
  return mins > 0 ? `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m` : "";
};

// Photos are served only to logged-in users. Fetching through the API client
// sends the session either way (cookie or bearer), so images show on web,
// in the mobile app and on plain-HTTP dev setups alike.
export function useAuthImage(url) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!url) return undefined;
    let objectUrl;
    let alive = true;
    api.get(url.replace(/^\/api/, ""), { responseType: "blob" })
      .then(({ data }) => { if (alive) { objectUrl = URL.createObjectURL(data); setSrc(objectUrl); } })
      .catch(() => {});
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url]);
  return src;
}

export async function uploadPhoto(file) {
  const body = new FormData();
  body.append("file", file);
  const { data } = await api.post("/uploads/", body, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
  return data.url;
}

// CSV that Excel opens directly (UTF-8 BOM so Swahili names keep their characters).
export function downloadCsv(filename, columns, rows) {
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [columns.map((c) => esc(c.label)).join(","), ...rows.map((r) => columns.map((c) => esc(c.value(r))).join(","))];
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const escapeHtml = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// Printable report in the KARIS document style -- PDF via the browser's (or
// the mobile app's) print dialog, same mechanism as invoices.
export function printTable({ title, subtitle, columns, rows, summary = [], system = "Daily Work Report System" }) {
  const win = window.open("", "_blank", "width=1100,height=900");
  if (!win) return;
  const head = columns.map((c) => `<th>${escapeHtml(c.label)}</th>`).join("");
  const body = rows.map((r) => `<tr>${columns.map((c) => `<td>${escapeHtml(c.value(r))}</td>`).join("")}</tr>`).join("");
  const sums = summary.map(([k, v]) => `<div><span>${escapeHtml(k)}</span><b>${escapeHtml(v)}</b></div>`).join("");
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
  @page { size: A4 landscape; margin: 14mm; }
  body { font-family: -apple-system, "Segoe UI", Helvetica, Arial, sans-serif; color: #0b1220; font-size: 11px; }
  header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid #293c98; padding-bottom: 8px; margin-bottom: 12px; }
  h1 { font-size: 18px; margin: 0; color: #293c98; } .sub { color: #475569; margin-top: 2px; }
  .co { text-align: right; font-size: 10px; color: #475569; } .co b { color: #0b1220; font-size: 12px; }
  .sums { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
  .sums div { border: 1px solid #e2e8f0; border-radius: 6px; padding: 6px 10px; } .sums span { color: #64748b; margin-right: 6px; }
  table { width: 100%; border-collapse: collapse; } th { background: #293c98; color: #fff; text-align: left; padding: 6px; font-size: 10px; }
  td { border-bottom: 1px solid #e2e8f0; padding: 6px; vertical-align: top; } tr { break-inside: avoid; }
  footer { margin-top: 14px; font-size: 9px; color: #64748b; }
  @media print { .noprint { display: none; } }
</style></head><body>
<header><div><h1>${escapeHtml(title)}</h1><div class="sub">${escapeHtml(subtitle || "")}</div></div>
<div class="co"><b>KARIBUNET LIMITED</b><br>${escapeHtml(system)}<br>Printed ${escapeHtml(new Date().toLocaleString("en-GB"))}</div></header>
${sums ? `<div class="sums">${sums}</div>` : ""}
<table><thead><tr>${head}</tr></thead><tbody>${body || `<tr><td colspan="${columns.length}">No records for this selection.</td></tr>`}</tbody></table>
<footer>Generated by KARIS.</footer>
<script>setTimeout(() => window.print(), 300);</script>
</body></html>`);
  win.document.close();
}

// "08:07" in the browser's own time (KaribuNet runs on EAT).
export const clock = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

// 0 -> "0 min", 75 -> "1 h 15 min"
export const minutesLabel = (mins) => {
  if (mins === null || mins === undefined) return "";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h} h${m ? ` ${m} min` : ""}` : `${m} min`;
};
